import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Users,
  Gift,
  Copy,
  Share2,
  Trophy,
  Sparkles,
  Check,
  ArrowRight,
  Star,
  MessageCircle,
} from "lucide-react";

const Referral = () => {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [referralRes, leaderboardRes] = await Promise.all([
        apiClient.get("/referral/code"),
        apiClient.get("/referral/leaderboard"),
      ]);
      setReferralData(referralRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareReferral = async () => {
    const shareData = {
      title: "Join BE Lifestyle!",
      text: `Use my referral code ${referralData?.referral_code} to get 300 bonus points when you sign up for BE Lifestyle!`,
      url: window.location.origin,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        copyReferralCode();
      }
    } else {
      copyReferralCode();
    }
  };

  const applyReferralCode = async () => {
    if (!applyCode.trim()) {
      toast.error("Please enter a referral code");
      return;
    }

    setApplying(true);
    try {
      const response = await apiClient.post("/referral/apply", {
        referral_code: applyCode.trim(),
      });
      toast.success(response.data.message);
      setApplyCode("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply referral code");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="referral-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">Refer & Earn</h1>
          </div>
          <p className="text-muted-foreground">
            Share the BE journey with friends and earn rewards together!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Referral Code Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 border-primary/20">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <h2 className="font-heading font-bold text-2xl mb-2">Your Referral Code</h2>
                    <p className="text-muted-foreground">
                      Share this code with friends to earn 300 points each!
                    </p>
                  </div>

                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="bg-card border-2 border-dashed border-primary rounded-xl px-8 py-4">
                      <span className="font-mono font-bold text-3xl text-primary tracking-wider" data-testid="referral-code">
                        {referralData?.referral_code || "Loading..."}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={copyReferralCode}
                      variant="outline"
                      className="rounded-full"
                      data-testid="copy-code-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Code
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={shareReferral}
                      className="rounded-full bg-primary hover:bg-primary/90"
                      data-testid="share-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share with Friends
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Stats Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              <Card>
                <CardContent className="p-6 text-center">
                  <Users className="w-10 h-10 mx-auto mb-3 text-primary" />
                  <div className="font-heading font-bold text-4xl text-primary mb-1">
                    {referralData?.referral_count || 0}
                  </div>
                  <p className="text-sm text-muted-foreground">Friends Referred</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Sparkles className="w-10 h-10 mx-auto mb-3 text-accent" />
                  <div className="font-heading font-bold text-4xl text-accent mb-1">
                    {referralData?.total_points_earned || 0}
                  </div>
                  <p className="text-sm text-muted-foreground">Points Earned</p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Apply Referral Code */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Have a Referral Code?</CardTitle>
                  <CardDescription>
                    Enter a friend's code to earn 300 bonus points
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter code (e.g., BE123ABC)"
                      value={applyCode}
                      onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                      className="font-mono"
                      data-testid="apply-code-input"
                    />
                    <Button
                      onClick={applyReferralCode}
                      disabled={applying || !applyCode.trim()}
                      data-testid="apply-code-btn"
                    >
                      {applying ? "Applying..." : "Apply"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Recent Referrals */}
            {referralData?.referrals?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Your Referrals</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {referralData.referrals.map((ref, index) => (
                        <div
                          key={ref.referral_id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-xl"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={ref.referred_picture} />
                              <AvatarFallback>{ref.referred_name?.[0] || "U"}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{ref.referred_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(ref.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-secondary/10 text-secondary border-secondary/20">
                            +{ref.referrer_points_awarded} pts
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* How It Works */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Star className="w-5 h-5 text-accent" />
                    How It Works
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { step: "1", text: "Share your unique referral code" },
                    { step: "2", text: "Friend signs up with your code" },
                    { step: "3", text: "You both earn 300 BE Points!" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary">{item.step}</span>
                      </div>
                      <p className="text-sm pt-1">{item.text}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            {/* Leaderboard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-accent" />
                    Top Referrers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {leaderboard.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      Be the first to refer friends!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {leaderboard.slice(0, 5).map((referrer, index) => (
                        <div
                          key={referrer.user_id}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">
                              {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : `${index + 1}.`}
                            </span>
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={referrer.picture} />
                              <AvatarFallback>{referrer.name?.[0] || "U"}</AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-sm truncate max-w-[100px]">
                              {referrer.name}
                            </span>
                          </div>
                          <Badge variant="outline">
                            {referrer.referral_count} refs
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Share Options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-primary" />
                    Quick Share
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      const text = `Join me on BE Lifestyle! Use my code ${referralData?.referral_code} and get 300 bonus points. ${window.location.origin}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
                    }}
                    data-testid="share-whatsapp"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      const text = `Join me on BE Lifestyle! Use my code ${referralData?.referral_code} and get 300 bonus points.`;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.origin)}`, "_blank");
                    }}
                    data-testid="share-twitter"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter/X
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Referral;
